import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#destinations", label: "Destinations" },
  { href: "#manifesto", label: "The Experience" },
  { href: "#perks", label: "VIP Perks" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#F9F6F0]/75 border-b border-black/5" : "bg-transparent"
        }`}
        data-testid="nav-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-baseline gap-3">
            <span className="font-serif-editorial text-xl md:text-2xl italic tracking-tight text-[#1A1A1A]">The Elevated Escape</span>
            <span className="eyebrow text-[#C5A059] hidden sm:inline">VIP · No Membership</span>
          </a>
          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <a key={l.href} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                 className="eyebrow text-[#1A1A1A] hover:text-[#C5A059] transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
          <a href="#inquiry" data-testid="nav-cta" className="hidden md:inline-flex ee-btn-primary !py-3 !px-6 !text-[11px]">
            Begin an Inquiry
          </a>
          <button
            className="md:hidden p-2 text-[#1A1A1A]"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.25} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#F9F6F0] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col h-full p-6">
              <div className="flex justify-between items-center">
                <span className="font-serif-editorial italic text-xl">The Elevated Escape</span>
                <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                  <X size={22} strokeWidth={1.25} />
                </button>
              </div>
              <nav className="flex-1 flex flex-col justify-center gap-8">
                {links.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.05 * i, duration: 0.5 }}
                    className="font-serif-editorial text-4xl italic text-[#1A1A1A]"
                    data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    {l.label}
                  </motion.a>
                ))}
                <motion.a
                  href="#inquiry"
                  onClick={() => setOpen(false)}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * links.length, duration: 0.5 }}
                  className="mt-4 self-start ee-btn-primary"
                  data-testid="mobile-link-begin-an-inquiry"
                >
                  Begin an Inquiry
                </motion.a>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
